import React from 'react';
import { Link } from 'react-router-dom';
import './ISO45001.css';

const ISO45001 = () => {
  return (
    <div className="page-container">
      <div className="page-content">
        <div className="page-main">
          <section className="page-section">
            <div className="page-breadcrumb">System Certification</div>
            <h1 className="page-title">ISO 45001 Occupational Health and Safety Management System</h1>
            <h2 className="page-subtitle">Reduce Risks, Increase Safety: Get to Know ISO 45001</h2>
            <div className="page-text-content">
              <p>
                As NQC (Nova Quality Certification Agency), we prioritize occupational health and safety 
                and aim to strengthen businesses in this area within the framework of the ISO 45001 
                standard. In this article, we will delve into what the ISO 45001 Occupational Health and 
                Safety Management System is, the advantages it provides to businesses, and how it is 
                implemented in detail.
              </p>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">What is ISO 45001?</h2>
            <div className="page-text-content">
              <p>
                The ISO 45001 Occupational Health and Safety Management System is a standard established 
                to protect the health and safety of employees in workplaces. This system aims to 
                continuously improve occupational health and safety performance and provide employees 
                with a healthy and safe working environment.
              </p>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">What is the ISO 45001 Standard?</h2>
            <div className="page-text-content">
              <p>
                ISO 45001 is an international occupational health and safety management system standard 
                used to assess, control, and continuously improve occupational health and safety 
                performance. The primary purpose of this standard is to identify, assess, and control 
                potential risks in workplaces by providing a framework to ensure the safety of employees. 
                ISO 45001 enables the determination, implementation, and maintenance of occupational 
                health and safety policies. Among the focal points of the standard are employee 
                participation, leadership, risk management, and legal compliance.
              </p>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">Key Elements of the ISO 45001 Standard:</h2>
            <div className="elements-list">
              <div className="element-item">
                <h3 className="element-title">Occupational Health and Safety Policy</h3>
                <p className="element-description">
                  ISO 45001 mandates that businesses establish and implement occupational health and 
                  safety policies. This policy should include the organization's commitment and objectives.
                </p>
              </div>
              <div className="element-item">
                <h3 className="element-title">Risk Assessment and Management</h3>
                <p className="element-description">
                  Businesses are required to conduct risk assessments, manage risks, identify, and 
                  prevent hazards to ensure workplace safety.
                </p>
              </div>
              <div className="element-item">
                <h3 className="element-title">Legal Compliance</h3>
                <p className="element-description">
                  ISO 45001 requires compliance with relevant occupational health and safety legal 
                  regulations and standards.
                </p>
              </div>
              <div className="element-item">
                <h3 className="element-title">Training and Awareness</h3>
                <p className="element-description">
                  It is important to train employees on health and safety topics and promote safe 
                  behaviors for accident prevention.
                </p>
              </div>
              <div className="element-item">
                <h3 className="element-title">Emergency Preparedness</h3>
                <p className="element-description">
                  Businesses must plan, implement, and test emergency preparedness for effective 
                  response to potential emergencies.
                </p>
              </div>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">Advantages of ISO 45001 Certification:</h2>
            <div className="advantages-list">
              <div className="advantage-item">
                <h3 className="advantage-title">Employee Safety and Health</h3>
                <p className="advantage-description">
                  ISO 45001 protects employees and improves workforce performance by ensuring a safe 
                  working environment.
                </p>
              </div>
              <div className="advantage-item">
                <h3 className="advantage-title">Legal and Financial Compliance</h3>
                <p className="advantage-description">
                  Compliance with legal regulations is ensured, helping businesses fulfill their 
                  responsibilities and avoid penalties.
                </p>
              </div>
              <div className="advantage-item">
                <h3 className="advantage-title">Reduction in Workplace Accidents</h3>
                <p className="advantage-description">
                  Risk assessment and effective management reduce accidents and improve overall safety 
                  in the workplace.
                </p>
              </div>
              <div className="advantage-item">
                <h3 className="advantage-title">Business Continuity</h3>
                <p className="advantage-description">
                  ISO 45001 safeguards business continuity by ensuring occupational health and safety, 
                  reducing operational disruptions.
                </p>
              </div>
              <div className="advantage-item">
                <h3 className="advantage-title">Corporate Reputation</h3>
                <p className="advantage-description">
                  Certification enhances reputation and instills trust in stakeholders, demonstrating 
                  commitment to employee well-being.
                </p>
              </div>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">How to Implement ISO 45001?</h2>
            <div className="page-text-content">
              <p>
                The implementation process of ISO 45001 may vary depending on the size and complexity of 
                the organization. However, generally, the implementation steps can be summarized as follows:
              </p>
            </div>
            <div className="steps-list">
              <div className="step-item">
                <h3 className="step-title">Initial Assessment</h3>
                <p className="step-description">
                  Evaluate existing occupational health and safety processes and identify areas for 
                  improvement.
                </p>
              </div>
              <div className="step-item">
                <h3 className="step-title">Policy and Process Development</h3>
                <p className="step-description">
                  Develop appropriate policies and processes for ISO 45001, including risk assessment 
                  and management procedures.
                </p>
              </div>
              <div className="step-item">
                <h3 className="step-title">Training and Communication</h3>
                <p className="step-description">
                  Train personnel on occupational health and safety topics and ensure continuous 
                  communication throughout the organization.
                </p>
              </div>
              <div className="step-item">
                <h3 className="step-title">Documentation and Monitoring</h3>
                <p className="step-description">
                  Create documentation compliant with ISO 45001 requirements and ensure regular 
                  monitoring of processes.
                </p>
              </div>
              <div className="step-item">
                <h3 className="step-title">Audit and Improvement</h3>
                <p className="step-description">
                  Regularly audit compliance with ISO 45001 standards and develop action plans for 
                  continuous improvement.
                </p>
              </div>
            </div>
            <div className="page-text-content">
              <p>
                ISO 45001 provides a framework for occupational health and safety, helping organizations 
                protect their employees, effectively manage risks, and establish a sustainable culture 
                of health and safety.
              </p>
            </div>
          </section>
        </div>

        <aside className="page-sidebar">
          <div className="sidebar-section">
            <h2 className="sidebar-title">Our Services</h2>
            <ul className="sidebar-links">
              <li><Link to="/system-certification">System Certification</Link></li>
              <li><Link to="/product-certification">Product Certification</Link></li>
              <li><Link to="/quality-certification">Quality Certification</Link></li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ISO45001;

